/**
 * Display-only star row in the tenant accent (`terra`) — the lodging
 * replacement for the hardcoded amber stars. For interactive rating input
 * (review forms) keep using `@/components/ui/RatingStars`.
 */
import { Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface RatingStarsProps {
  rating: number;
  max?: number;
  size?: 'sm' | 'md';
  className?: string;
}

const sizes = { sm: 'h-4 w-4', md: 'h-5 w-5' };

export default function RatingStars({ rating, max = 5, size = 'sm', className }: RatingStarsProps) {
  const { t } = useTranslation('properties');
  return (
    <span
      role="img"
      aria-label={t('shared.ratingAriaLabel', { rating: rating.toFixed(1), max })}
      className={cn('inline-flex items-center gap-0.5', className)}
    >
      {Array.from({ length: max }, (_, i) => (
        <Star
          key={i}
          aria-hidden="true"
          className={cn(
            sizes[size],
            i < Math.round(rating) ? 'fill-terra text-terra' : 'fill-none text-line-strong',
          )}
        />
      ))}
    </span>
  );
}
